import React, { useState } from "react";
import { Formik } from "formik";

import ContainerFormButtons from "./ContainerFormButtons";
import FormikNumberInput from "../../../../formik-form-components/FormikNumberInput";
import FormikFormLine from "../../../../formik-form-components/FormikFormLine";

import { Container } from "../../../../../reducers/componentSearch";
import { FormContainer } from "../../../../../constants/Styles";
import { baseContainerSchema, ContainerDetailsFormValues } from "./constants";
import {
  datetimeStringToHumanReadableDate,
  formatSmallCurrency,
} from "../../../../../utils/valueFormatting";
import { CURRENCY } from "../../../../../reducers/uploadedBoard";
import useFetchAndTrace from "../../../../../utils/hooks/fetchWithTrace";
import settings from "../../../../../constants/settings";
import { USD } from "../../../../../constants/orders";
import { FieldTypes } from "../../../../formik-form-components/FormikFormLine/constants";
import BinBarcodeField from "./BinBarcodeField";
import { useCustomAlert } from "../../../../../utils/hooks/alerts";
import useContainerHistorySummary from "../../../../../utils/hooks/inventory/containerHistory";
import FormikSelect from "../../../../formik-form-components/FormikSelect";
import { PlacementType } from "../../../../../reducers/models/parts";
import { useFlashPCBSettings } from "../../../../../utils/hooks/commonly-stored/flashPCBSettings";
import usePart from "../../../../../utils/hooks/commonly-stored/parts";
import LoadingIcon from "../../../../common/LoadingIcon";
import { CONTAINER_TYPE_OPTIONS, MSLLevel } from "./AddNewContainer";
import * as Yup from "yup";
import { MAX_CONTAINER_QUANTITY } from "./UpdateContainerQuantity";
import { FLASHPCB_BIN_BARCODE_REGEX } from "../../../../internal/parts/InventoryBinForm";
import FormikRadioOptions from "../../../../formik-form-components/FormikRadioOptions";

const formatDateYYYYMMDD = (date: string) => date.slice(0, 10);

const ContainerDetails = ({
  partId,
  selectedContainer,
  onCancel,
  onFinishedEditing,
}: {
  partId: string;
  selectedContainer: Container;
  onCancel: () => void;
  onFinishedEditing: () => void;
}) => {
  const customAlert = useCustomAlert();
  const fetchAndTrace = useFetchAndTrace();
  const { fetchHistory } = useContainerHistorySummary();
  const [loading, setLoading] = useState(false);
  const flashPCBSettings = useFlashPCBSettings();
  const errorHandler = () => {
    setLoading(false);
    customAlert("Failed to update container");
  };
  const { part } = usePart(partId);

  const modifyContainerHandler = async (response: Response) => {
    if (response.ok) {
      fetchHistory();
      onFinishedEditing();
    } else if (response.status === 400) {
      const { detail } = (await response.json()) as { detail: string };
      customAlert(detail);
    } else {
      throw Error("Failed to update container.");
    }
    setLoading(false);
  };

  const schema = baseContainerSchema.shape({
    lastCountedAmount: Yup.number()
      .integer("Last counted amount must be an integer")
      .required("Last counted amount is required")
      .min(0, "Last counted amount cannot be negative")
      .max(
        MAX_CONTAINER_QUANTITY,
        `Quantity cannot exceed ${MAX_CONTAINER_QUANTITY.toLocaleString()}`,
      )
      .when("flashpcbBinBarcode", {
        is: (value: string | null) => !value,
        then: (schema) =>
          schema.max(
            0,
            "If you are omitting a bin for this part then you must zero out the count",
          ),
      }),
    containerType: Yup.mixed().oneOf(
      Object.values(CONTAINER_TYPE_OPTIONS).map((option) => option.value),
    ),
    flashpcbBinBarcode: Yup.string()
      .nullable()
      .matches(new RegExp(FLASHPCB_BIN_BARCODE_REGEX)),
  });
  const onSubmit = (values: ContainerDetailsFormValues) => {
    setLoading(true);
    void fetchAndTrace(
      `${settings.api_url}/inventory/containers`,
      modifyContainerHandler,
      errorHandler,
      {
        body: JSON.stringify({
          part_id: partId,
          container_type: values.containerType,
          last_counted_amount: values.lastCountedAmount,
          notes: values.notes,
          nickname: values.nickname,
          price_info: {
            unit_price: values.unitPrice,
            quantity: 1,
            currency: USD,
          },
          order_date: values.orderDate || null,
          vendor: values.vendor,
          moisture_sensitivity_level: values.moistureSensitivityLevel,
          flashpcb_container_barcode: values.flashpcbContainerBarcode,
          flashpcb_bin_barcode: values.flashpcbBinBarcode,
          part_allocations: values.allocations,
          spent_part_allocations: values.spentAllocations,
          country_code: values.countryCode || null,
          date_code: values.dateCode || null,
          lot_number: values.lotNumber || null,
          reel_width_mm: values.reelWidthMM || null,
          reel_pitch_mm: values.reelPitchMM || null,
          part_placement_type: values.partPlacementType || null,
        }),
        method: "PUT",
      },
    );
  };

  if (part === null) {
    return <LoadingIcon text={"Loading part..."} />;
  }

  return (
    <Formik
      initialValues={{
        containerType: selectedContainer.container_type,
        lastCountedAmount: selectedContainer.last_counted_amount,
        nickname: selectedContainer.nickname || "",
        notes: selectedContainer.notes || "",
        unitPrice: selectedContainer.price_info.unit_price,
        orderDate: formatDateYYYYMMDD(selectedContainer.order_date || ""),
        vendor: selectedContainer.vendor || "",
        moistureSensitivityLevel:
          selectedContainer.moisture_sensitivity_level || "",
        flashpcbContainerBarcode: selectedContainer.flashpcb_container_barcode,
        flashpcbBinBarcode: selectedContainer.flashpcb_bin_barcode,
        allocations: selectedContainer.part_allocations,
        spentAllocations: selectedContainer.spent_part_allocations,
        countryCode: selectedContainer.country_code || "",
        lotNumber: selectedContainer.lot_number || "",
        dateCode: selectedContainer.date_code || "",
        reelWidthMM: part.reel_details.reel_width_mm || 0,
        reelPitchMM: part.reel_details.reel_pitch_mm || 0,
        partPlacementType: part.placement_type || "",
      }}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      {({ handleSubmit, isValid, dirty, values }) => {
        return (
          <FormContainer>
            <FormikFormLine
              name="flashpcbContainerBarcode"
              label="FlashPCB Container Barcode"
              isRequired={true}
              disabled={true}
            />
            <FormikRadioOptions
              name={"containerType"}
              options={Object.values(CONTAINER_TYPE_OPTIONS)}
              label={"Container type:"}
            />
            <BinBarcodeField binBarcode={values.flashpcbBinBarcode} />
            <FormikNumberInput
              name="lastCountedAmount"
              labelComponent={`Last counted amount (${selectedContainer.last_counted_amount}). Available inventory (${selectedContainer.available_inventory_level})`}
              disabled={false}
            />
            <FormikFormLine
              name="notes"
              label="Notes"
              isRequired={false}
              isTextArea={true}
            />
            <FormikFormLine
              name="nickname"
              label="Nickname"
              isRequired={false}
            />
            <FormikNumberInput
              name="unitPrice"
              label={`Unit price (${formatSmallCurrency(
                selectedContainer.price_info.unit_price,
                selectedContainer.price_info.currency as CURRENCY,
              )})`}
              disabled={false}
            />
            <FormikFormLine
              name="vendor"
              label={`Vendor${
                selectedContainer.vendor ? ` (${selectedContainer.vendor})` : ""
              }`}
              isRequired={false}
            />
            <FormikSelect
              name={"reelWidthMM"}
              label={"Reel width (mm)"}
              options={
                flashPCBSettings?.reel_size_options.reel_width_mm.map(
                  (width) => ({ label: `${width} mm`, value: width }),
                ) || []
              }
            />
            <FormikSelect
              name={"reelPitchMM"}
              label={"Reel pitch (mm)"}
              options={
                flashPCBSettings?.reel_size_options.reel_pitch_mm.map(
                  (pitch) => ({ label: `${pitch} mm`, value: pitch }),
                ) || []
              }
            />
            <FormikSelect
              name={"partPlacementType"}
              label={"Part placement type"}
              options={Object.values(PlacementType).map((option) => ({
                label: option,
                value: option,
              }))}
            />
            <FormikFormLine name={"dateCode"} label={"Date code"} />
            <FormikFormLine name={"countryCode"} label={"Country code"} />
            <FormikFormLine name={"lotNumber"} label={"Lot number"} />
            <FormikFormLine
              name="orderDate"
              label={`Order date${
                selectedContainer.order_date
                  ? ` (${datetimeStringToHumanReadableDate(
                      selectedContainer.order_date,
                    )})`
                  : ""
              }`}
              type={FieldTypes.date}
              isRequired={false}
            />
            <FormikSelect
              name={"moistureSensitivityLevel"}
              label={"Moisture sensitivity level"}
              options={Object.values(MSLLevel).map((msl) => ({
                value: msl,
                label: msl,
              }))}
            />
            <ContainerFormButtons
              onCancel={onCancel}
              loading={loading}
              isDisabled={!(isValid && dirty)}
              onSubmit={handleSubmit}
            />
          </FormContainer>
        );
      }}
    </Formik>
  );
};

export default ContainerDetails;
